import { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageCircle,
  Send,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { trackEvent } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Editable contact data
export const BETEC_CONTACT = {
  email: "contacto@betec.cl",
  phone: "+56 9 3524 2662",
  whatsapp: "56935242662", // sin +, sin espacios — usado por wa.me/
  city: "Todo el territorio nacional · Chile",
  hours_office: "Lun a Vie · 09:00 – 18:00",
  hours_support: "Soporte 24 / 7 / 365",
};

const INTEREST_OPTIONS = [
  { value: "soluciones", label: "Soluciones (POS, Autoservicio, etc.)" },
  { value: "hardware", label: "Hardware · venta de equipos" },
  { value: "servicios", label: "Servicios · Soporte e Implementación" },
  { value: "redes", label: "Redes · Cableado y Certificación" },
  { value: "soporte", label: "Soporte técnico activo" },
  { value: "otro", label: "Otro" },
];

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  interest: "soluciones",
  message: "",
};

export const Contacto = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const err = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      err.name = "Ingresa tu nombre.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      err.email = "Ingresa un correo válido.";
    if (!form.message.trim() || form.message.trim().length < 4)
      err.message = "Cuéntanos brevemente qué necesitas.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revisa los campos requeridos.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.interest,
        message: form.message.trim(),
        source: "web-home",
      });
      setSent(true);
      setForm(initialForm);
      toast.success("Mensaje enviado. Te contactaremos a la brevedad.");
      trackEvent("lead_submit", { interest: form.interest });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "No pudimos enviar tu mensaje. Intenta nuevamente o escríbenos por WhatsApp.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      className="relative bg-[#05162a] text-white py-24 md:py-32 overflow-hidden"
    >
      {/* Decorative grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage:
            "radial-gradient(ellipse 80% 70% at 50% 40%, black 50%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 70% at 50% 40%, black 50%, transparent 100%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-32 right-1/4 w-[480px] h-[480px] rounded-full bg-[#1FA08A]/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-[#1FA08A]/10 blur-[120px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-6 text-[11px] uppercase tracking-[0.32em] font-mono">
            <span className="text-[#2bc9ae] font-semibold">/ 06</span>
            <span className="h-px w-10 bg-white/25" />
            <span className="text-slate-300/70 font-semibold">
              Contacto · Hablemos
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <div className="lg:col-span-7">
              <h2
                data-testid="contacto-heading"
                className="font-heading text-white text-4xl sm:text-5xl md:text-[56px] lg:text-[68px] leading-[1.02] tracking-tighter font-bold"
              >
                Cuéntanos qué necesitas.{" "}
                <span className="text-[#2bc9ae]">Te respondemos hoy.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p
                data-testid="contacto-subtitle"
                className="text-slate-300/85 text-base md:text-lg leading-relaxed max-w-md lg:ml-auto"
              >
                Cotizaciones, soporte o consultas técnicas. Un especialista
                Betec se pone en contacto en menos de{" "}
                <span className="text-white font-semibold">24 horas hábiles</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Two-column layout: contact info + form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Contact info column */}
          <div className="lg:col-span-5 space-y-4">
            <ContactInfoCard
              Icon={Mail}
              label="Correo"
              value={BETEC_CONTACT.email}
              href={`mailto:${BETEC_CONTACT.email}`}
              testid="contact-email"
            />
            <ContactInfoCard
              Icon={Phone}
              label="Teléfono"
              value={BETEC_CONTACT.phone}
              href={`tel:${BETEC_CONTACT.phone.replace(/\s/g, "")}`}
              testid="contact-phone"
            />
            <ContactInfoCard
              Icon={MessageCircle}
              label="WhatsApp"
              value="Escríbenos en línea"
              href={`https://wa.me/${BETEC_CONTACT.whatsapp}?text=${encodeURIComponent(
                "Hola Betec, me gustaría cotizar / consultar sobre..."
              )}`}
              external
              accent
              testid="contact-whatsapp"
            />
            <ContactInfoCard
              Icon={MapPin}
              label="Cobertura"
              value={BETEC_CONTACT.city}
              testid="contact-location"
            />
            <ContactInfoCard
              Icon={Clock}
              label="Horarios"
              value={`${BETEC_CONTACT.hours_office}  ·  ${BETEC_CONTACT.hours_support}`}
              testid="contact-hours"
            />
          </div>

          {/* Form column */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              noValidate
              className="relative rounded-md bg-white text-slate-900 p-7 md:p-10 border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
            >
              {/* Pixel accent */}
              <div
                aria-hidden
                className="absolute top-5 right-5 grid grid-cols-3 gap-1.5 opacity-60"
              >
                {[1, 1, 0, 1, 1, 1, 0, 1, 1].map((v, i) => (
                  <span
                    key={i}
                    className={`w-2 h-2 ${
                      v ? "bg-[#1FA08A]" : "bg-transparent"
                    }`}
                  />
                ))}
              </div>

              <div className="text-[11px] uppercase tracking-[0.28em] font-mono font-semibold text-[#1FA08A] mb-2">
                Solicita tu cotización
              </div>
              <h3 className="font-heading text-[#0A2A4E] text-2xl md:text-3xl font-semibold tracking-tight mb-7">
                Formulario de contacto
              </h3>

              {sent ? (
                <SuccessState onReset={() => setSent(false)} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
                  <Field
                    label="Nombre"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                    testid="form-name"
                    placeholder="Tu nombre completo"
                  />
                  <Field
                    label="Empresa"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    error={errors.company}
                    testid="form-company"
                    placeholder="Razón social u organización"
                  />
                  <Field
                    label="Correo electrónico"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                    testid="form-email"
                    placeholder="tu@empresa.cl"
                  />
                  <Field
                    label="Teléfono"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    error={errors.phone}
                    testid="form-phone"
                    placeholder="+56 9 ..."
                  />

                  {/* Interest select */}
                  <div className="md:col-span-2">
                    <label
                      htmlFor="interest"
                      className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2"
                    >
                      Te interesa
                    </label>
                    <div className="relative">
                      <select
                        id="interest"
                        name="interest"
                        value={form.interest}
                        onChange={handleChange}
                        data-testid="form-interest"
                        className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 pr-10 text-[15px] text-slate-900 focus:outline-none focus:border-[#1FA08A] focus:bg-white transition-colors"
                      >
                        {INTEREST_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <span
                        aria-hidden
                        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 w-2 h-2 border-r-2 border-b-2 border-slate-400 rotate-45"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="md:col-span-2">
                    <label
                      htmlFor="message"
                      className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2"
                    >
                      Mensaje{" "}
                      <span className="text-[#1FA08A]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="form-message"
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Cuéntanos brevemente: tipo de proyecto, sucursales involucradas, plazos, etc."
                      className={`w-full bg-slate-50 border rounded-sm px-4 py-3 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors resize-none ${
                        errors.message
                          ? "border-rose-400 focus:border-rose-500"
                          : "border-slate-200 focus:border-[#1FA08A]"
                      }`}
                    />
                    {errors.message && (
                      <p className="mt-1.5 text-[12px] text-rose-500">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {/* Privacy note */}
                  <p className="md:col-span-2 text-[11.5px] text-slate-500 leading-relaxed">
                    Al enviar este formulario aceptas que Betec utilice tus
                    datos únicamente para responder a tu solicitud. Nunca los
                    compartimos con terceros.
                  </p>

                  {/* Submit */}
                  <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-1">
                    <button
                      type="submit"
                      data-testid="form-submit"
                      disabled={sending}
                      className="cta-cotizar group inline-flex items-center justify-center gap-2.5 bg-[#1FA08A] hover:bg-[#167d6c] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-3.5 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-[0_18px_50px_-10px_rgba(43,201,174,0.7)]"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar mensaje
                          <span aria-hidden className="cta-dot" />
                          <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </>
                      )}
                    </button>
                    <span className="text-[12px] text-slate-500">
                      O escríbenos directo a{" "}
                      <a
                        href={`mailto:${BETEC_CONTACT.email}`}
                        className="text-[#0A2A4E] font-semibold underline-offset-4 hover:underline"
                      >
                        {BETEC_CONTACT.email}
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactInfoCard = ({ Icon, label, value, href, external, accent, testid }) => {
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? external
      ? { href, target: "_blank", rel: "noopener noreferrer" }
      : { href }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={testid}
      className={`group flex items-start gap-4 rounded-md border border-white/10 backdrop-blur-sm p-5 transition-all duration-300 ${
        accent
          ? "bg-[#1FA08A]/15 hover:bg-[#1FA08A]/25 border-[#1FA08A]/40"
          : "bg-white/[0.04] hover:bg-white/[0.07]"
      } ${href ? "hover:-translate-y-0.5" : ""}`}
    >
      <div
        className={`inline-flex items-center justify-center w-11 h-11 rounded-md shrink-0 ${
          accent
            ? "bg-[#1FA08A] text-white"
            : "bg-white/[0.06] text-[#2bc9ae] border border-white/10"
        }`}
      >
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </div>
      <div>
        <div
          className={`text-[10.5px] uppercase tracking-[0.22em] font-mono font-semibold mb-1 ${
            accent ? "text-white/85" : "text-[#2bc9ae]"
          }`}
        >
          {label}
        </div>
        <div className="text-[15px] md:text-base text-white font-medium leading-snug">
          {value}
        </div>
      </div>
    </Wrapper>
  );
};

const Field = ({ label, name, value, onChange, error, required, type = "text", testid, placeholder }) => (
  <div>
    <label
      htmlFor={name}
      className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2"
    >
      {label} {required && <span className="text-[#1FA08A]">*</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className={`w-full bg-slate-50 border rounded-sm px-4 py-3 text-[15px] text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white transition-colors ${
        error
          ? "border-rose-400 focus:border-rose-500"
          : "border-slate-200 focus:border-[#1FA08A]"
      }`}
    />
    {error && <p className="mt-1.5 text-[12px] text-rose-500">{error}</p>}
  </div>
);

const SuccessState = ({ onReset }) => (
  <div
    data-testid="form-success"
    className="flex flex-col items-start gap-4 py-6"
  >
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-[#1FA08A]/15 text-[#1FA08A]">
      <CheckCircle2 className="w-8 h-8" strokeWidth={1.5} />
    </div>
    <div>
      <h4 className="font-heading text-[#0A2A4E] text-xl font-semibold mb-1.5">
        ¡Mensaje enviado!
      </h4>
      <p className="text-slate-600 text-[14.5px] max-w-md leading-relaxed">
        Recibimos tu solicitud. Un especialista Betec se pondrá en contacto
        contigo en menos de 24 horas hábiles.
      </p>
    </div>
    <button
      type="button"
      onClick={onReset}
      className="text-[12.5px] uppercase tracking-[0.18em] font-semibold text-[#0A2A4E] hover:text-[#1FA08A] transition-colors"
    >
      Enviar otro mensaje →
    </button>
  </div>
);

export default Contacto;
